import type { AfterViewInit, DoCheck, OnDestroy } from '@angular/core';
import { Directive, ElementRef, Input } from '@angular/core';

import { fromEvent as observableFromEvent, Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[rtTextareaAutosize]',
  exportAs: '[rtTextareaAutosize]',
  host: {
    class: 'rt-textarea-autosize',
    rows: '1',
  },
  standalone: true,
})
export class TextareaAutosizeDirective implements AfterViewInit, DoCheck, OnDestroy {
  private readonly ngOnDestroy$ = new Subject<void>();

  private previousValue?: string;
  private initialHeight: string | null;

  private _minRows: number;
  private _maxRows: number;
  private _enabled: boolean;

  private previousMinRows: number = -1;

  private textareaElement: HTMLTextAreaElement;

  @Input('rtAutosizeMinRows')
  public get minRows(): number {
    return this._minRows;
  }

  public set minRows(value: number) {
    this._minRows = value;
    this.setMinHeight();
  }

  @Input('rtAutosizeMaxRows')
  public get maxRows(): number {
    return this._maxRows;
  }

  public set maxRows(value: number) {
    this._maxRows = value;
    this.setMaxHeight();
  }

  @Input('rtTextareaAutosize')
  public get enabled(): boolean {
    return this._enabled;
  }

  public set enabled(value: boolean) {
    if (this._enabled !== value) {
      this._enabled = value;
      this._enabled ? this.resizeToFitContent(true) : this.reset();
    }
  }

  private cachedLineHeight: number;

  constructor(private elementRef: ElementRef) {
    this.textareaElement = <HTMLTextAreaElement>this.elementRef.nativeElement;
  }

  public ngAfterViewInit(): void {
    this.initialHeight = this.textareaElement.style.height;

    requestAnimationFrame(() => {
      this.resizeToFitContent(true);
    });

    observableFromEvent(window, 'resize')
      .pipe(auditTime(16), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.resizeToFitContent(true));
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngDoCheck(): void {
    this.resizeToFitContent();
  }

  public resizeToFitContent(force: boolean = false): void {
    if (!this._enabled) {
      return;
    }

    this.cacheTextareaLineHeight();

    if (!this.cachedLineHeight) {
      return;
    }

    const textarea = <HTMLTextAreaElement>this.elementRef.nativeElement;
    const value = textarea.value;

    if (!force && this._minRows === this.previousMinRows && value === this.previousValue) {
      return;
    }

    const placeholderText = textarea.placeholder;

    textarea.classList.add('rt-textarea-autosize-measuring');
    textarea.placeholder = '';

    // Temporary set height to auto before height measure
    textarea.style.height = 'auto';

    // The rt-textarea-autosize-measuring class includes a 2px padding to workaround an issue with
    // Chrome, so we account for that extra space here by subtracting 4 (2px top + 2px bottom).
    const height = textarea.scrollHeight - 4;
    textarea.style.height = `${height}px`;
    textarea.classList.remove('rt-textarea-autosize-measuring');
    textarea.placeholder = placeholderText;

    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => this.scrollToCaretPosition(textarea));
    } else {
      setTimeout(() => this.scrollToCaretPosition(textarea));
    }

    this.previousValue = value;
    this.previousMinRows = this._minRows;
  }

  public reset(): void {
    if (this.initialHeight === undefined) {
      return;
    }
    this.textareaElement.style.height = this.initialHeight!;
  }

  private scrollToCaretPosition(textarea: HTMLTextAreaElement): void {
    const { selectionStart, selectionEnd } = textarea;
    if (!this.ngOnDestroy$.isStopped && document.activeElement === textarea) {
      textarea.setSelectionRange(selectionStart, selectionEnd);
    }
  }

  private cacheTextareaLineHeight(): void {
    if (this.cachedLineHeight) {
      return;
    }

    const textareaClone = <HTMLTextAreaElement>this.textareaElement.cloneNode(false);
    textareaClone.rows = 1;

    textareaClone.style.position = 'absolute';
    textareaClone.style.visibility = 'hidden';
    textareaClone.style.border = 'none';
    textareaClone.style.padding = '0';
    textareaClone.style.height = '';
    textareaClone.style.minHeight = '';
    textareaClone.style.maxHeight = '';

    textareaClone.style.overflow = 'hidden';

    this.textareaElement.parentNode!.appendChild(textareaClone);
    this.cachedLineHeight = textareaClone.clientHeight;
    this.textareaElement.parentNode!.removeChild(textareaClone);

    this.setMinHeight();
    this.setMaxHeight();
  }

  private setMinHeight(): void {
    const minHeight =
      this.minRows && this.cachedLineHeight ? `${this.minRows * this.cachedLineHeight}px` : null;

    if (minHeight) {
      this.textareaElement.style.minHeight = minHeight;
    }
  }

  private setMaxHeight(): void {
    const maxHeight =
      this.maxRows && this.cachedLineHeight ? `${this.maxRows * this.cachedLineHeight}px` : null;

    if (maxHeight) {
      this.textareaElement.style.maxHeight = maxHeight;
    }
  }
}
